import React, { useState } from 'react';
import {
  CommunityCardRow as CommunityCardRowComponent,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { Text, GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import { createPortal } from 'react-dom';

import CommunityCardRowProps from './CommunityCardRow.props';
import {
  BaseProductWithChildren,
  BaseProductWithPriceOptions,
  SearchBoxCsr,
  transformPropsIntoPropertyCards,
} from '@greystar/ui';
import {
  doLocationSearchQuery,
  getDictionaryEntry,
  getEngine,
  SEARCHBOX_SUBTITLE_FALLBACK,
  transformPropertiesBase,
  isLocationGuidePage,
  doMasterIdSearchQuery,
} from '@greystar/lib';

import {
  SearchBox as HeadlessSearchBox,
  InstantProducts as HeadlessInstantProducts,
  buildStandaloneSearchBox,
  buildInstantProducts,
} from '@coveo/headless/commerce';
import { useI18n } from 'next-localization';

type PropsWithProperties = CommunityCardRowProps & {
  properties: BaseProductWithPriceOptions[];
};

const CommunityCardRow = (props: PropsWithProperties) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useI18n();
  const isPageEditing = props.sitecoreContext?.pageEditing;
  const validHeadingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
  const headingTag = validHeadingTags.includes(
    props.params?.headingTag as (typeof validHeadingTags)[number]
  )
    ? (props.params?.headingTag as (typeof validHeadingTags)[number])
    : 'h2';

  if (!isPageEditing && (!props.properties || props.properties.length <= 0)) return;

  const header = <Text field={props?.fields?.heading} />;
  const subtitle = <Text field={props?.fields?.title} />;
  const bodyCopy = <Text field={props?.fields?.bodyCopy} />;

  const controllerSearchBox: HeadlessSearchBox = buildStandaloneSearchBox(getEngine(), {
    options: {
      redirectionUrl: '/s',
      id: 'community-card-searchbox',
    },
  });

  const controllerInstantProducts: HeadlessInstantProducts = buildInstantProducts(getEngine(), {
    options: {
      searchBoxId: 'community-card-searchbox',
    },
  });

  const properties = transformPropsIntoPropertyCards(props.properties, '');

  return (
    <PageSection
      pushUpPageSection={props.params?.pushUpPageSection}
      isRoundedTop
      paddingTop={props.params?.paddingTop ?? undefined}
    >
      <PageSectionContent>
        <CommunityCardRowComponent
          propertyCards={properties}
          onSearchAllCommunities={() => setIsModalOpen(true)}
          header={header}
          subtitle={subtitle}
          bodyCopy={bodyCopy}
          headerTag={headingTag}
        />
        {isModalOpen &&
          createPortal(
            <SearchBoxCsr
              variant="modal"
              controllerSearchBox={controllerSearchBox}
              controllerInstantProducts={controllerInstantProducts}
              headerTitle={getDictionaryEntry(t('searchbox_title'), 'Search for homes')}
              headerSubtitle={getDictionaryEntry(
                t('searchbox_subtitle'),
                SEARCHBOX_SUBTITLE_FALLBACK
              )}
              onClose={() => setIsModalOpen((prev) => !prev)}
              searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
              searchResultsTitle=""
            />,
            document.body
          )}
      </PageSectionContent>
    </PageSection>
  );
};

export default CommunityCardRow;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const fields = rendering?.fields;
  const templateId = (layoutData?.sitecore.route?.templateId ?? '').toUpperCase();
  const pageTitle = (layoutData?.sitecore.route?.fields?.title as { value?: string })?.value ?? '';

  const masterIds = fields?.masterIds as { name: string }[] | undefined;

  let propertiesArr: BaseProductWithChildren[] = [];
  let properties: BaseProductWithPriceOptions[] = [];

  try {
    if (isLocationGuidePage(templateId) && pageTitle) {
      const res = await doLocationSearchQuery(pageTitle);
      if ('products' in res && Array.isArray(res.products)) {
        const propertyChildren = res.products.map((product) => product.children).flat();
        propertiesArr = [...res.products, ...propertyChildren];
      }
    } else if (masterIds?.length) {
      const masterIdsQuery = masterIds.map((id: { name?: string }) => id.name ?? '');
      const res = await doMasterIdSearchQuery(masterIdsQuery);
      if ('products' in res && Array.isArray(res.products)) {
        propertiesArr = res.products;
      }
    }
  } catch (err) {
    console.error('Error in getStaticProps for CommunityCardRow:', err);
  }

  if (propertiesArr && propertiesArr.length > 0) {
    properties = transformPropertiesBase(propertiesArr);
  }

  if (!properties || properties.length <= 0)
    return {
      rendering,
      layoutData,
      fields,
      params: rendering?.params || {},
      properties: [],
    };

  return {
    rendering,
    layoutData,
    fields,
    params: rendering?.params || {},
    properties,
  };
};
